import { useState, useCallback } from 'react';
import { useOne } from '@refinedev/core';
import { ExternalLink, Loader2 } from 'lucide-react';
import { MastersClubIcon } from '@/components/icons/masters-club-icon';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { User } from '@/resources/users/types';
import { MessageList } from './MessageList';
import { MessageComposer } from './MessageComposer';
import { SelectionToolbar } from './SelectionToolbar';
import { ContextMenu } from './ContextMenu';
import { ChatAvatar } from './ChatAvatar';
import { EditMessageModal } from './EditMessageModal';
import { ForwardMessageModal } from './ForwardMessageModal';
import { PublishWithOrderModal } from './PublishWithOrderModal';
import { useMessageSelection } from '../hooks';
import type { TelegramChat, TelegramMessage, SendFileData, SendStickerData, SendGifData, ForwardOptions } from '../types';

interface ChatViewProps {
  chat: TelegramChat | null;
  messages: TelegramMessage[];
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  isSending: boolean;
  isUploadingFile?: boolean;
  onSendMessage: (text: string, replyToMessageId?: number) => void;
  onSendFile?: (data: SendFileData) => void;
  onSendSticker?: (data: SendStickerData) => void;
  onSendGif?: (data: SendGifData) => void;
  onEditMessage: (messageId: number, text: string) => Promise<unknown>;
  onDeleteMessage: (messageId: number) => void;
  onForwardMessage: (messageId: number, toChatId: number, options?: ForwardOptions) => Promise<unknown>;
  onForwardMessages?: (messageIds: number[], toChatId: number, options?: ForwardOptions) => Promise<unknown>;
  onMarkPublic: (messageIds: number[], orderId?: number, isPublic?: boolean) => Promise<TelegramMessage[]>;
  onLinkToOrder: (chatId: number) => void;
  isMessageRead: (message: TelegramMessage) => boolean;
  onLoadMore?: () => Promise<boolean>;
  /** Текст typing индикатора (например "печатает...") */
  typingText?: string | null;
}

export function ChatView({
  chat,
  messages,
  isLoading,
  isLoadingMore,
  hasMore,
  isSending,
  isUploadingFile,
  onSendMessage,
  onSendFile,
  onSendSticker,
  onSendGif,
  onEditMessage,
  onDeleteMessage,
  onForwardMessage,
  onForwardMessages,
  onMarkPublic,
  onLinkToOrder,
  isMessageRead,
  onLoadMore,
  typingText,
}: ChatViewProps) {
  const {
    selectedIds,
    toggleSelection,
    clearSelection,
  } = useMessageSelection(messages);

  const linkedUserResult = useOne<User>({
    resource: 'users',
    id: Number(chat?.linkedUserId),
    queryOptions: {
      enabled: !!chat?.linkedUserId,
    },
  });
  const linkedUser = linkedUserResult.result;

  const [replyTo, setReplyTo] = useState<TelegramMessage | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    message: TelegramMessage;
    position: { x: number; y: number };
  } | null>(null);

  // Edit/Forward/Publish modals
  const [editingMessage, setEditingMessage] = useState<TelegramMessage | null>(null);
  const [forwardingMessageIds, setForwardingMessageIds] = useState<number[]>([]);
  const [publishingMessageIds, setPublishingMessageIds] = useState<number[]>([]);

  // Handle context menu
  const handleContextMenu = useCallback((e: React.MouseEvent, message: TelegramMessage) => {
    e.preventDefault();
    setContextMenu({
      message,
      position: { x: e.clientX, y: e.clientY },
    });
  }, []);

  // Handle message toggle with shift support
  const handleToggleSelection = useCallback((messageId: number, shiftKey: boolean) => {
    toggleSelection(messageId, shiftKey);
  }, [toggleSelection]);

  // Context menu actions
  const handleReply = useCallback((message: TelegramMessage) => {
    setReplyTo(message);
    clearSelection();
  }, [clearSelection]);

  const handleForward = useCallback((message: TelegramMessage) => {
    setForwardingMessageIds([message.id]);
    setContextMenu(null);
  }, []);

  const handleCopy = useCallback((message: TelegramMessage) => {
    if (message.text) {
      navigator.clipboard.writeText(message.text);
    }
  }, []);

  const handleEdit = useCallback((message: TelegramMessage) => {
    setEditingMessage(message);
    setContextMenu(null);
  }, []);

  const handleDelete = useCallback((message: TelegramMessage) => {
    onDeleteMessage(message.id);
  }, [onDeleteMessage]);

  const handleMarkPublicSingle = useCallback((message: TelegramMessage) => {
    if (message.isPublic) {
      // Снимаем публичность — вызываем напрямую без модалки
      onMarkPublic([message.id], undefined, false);
    } else {
      // Делаем публичным — открываем модалку с выбором заказа
      setPublishingMessageIds([message.id]);
    }
    setContextMenu(null);
  }, [onMarkPublic]);

  const handleLinkToOrderSingle = useCallback(() => {
    if (chat) {
      onLinkToOrder(chat.id);
    }
  }, [chat, onLinkToOrder]);

  // Toolbar actions
  const handleToolbarReply = useCallback(() => {
    const selectedMessage = messages.find(m => selectedIds.has(m.id));
    if (selectedMessage) {
      setReplyTo(selectedMessage);
      clearSelection();
    }
  }, [messages, selectedIds, clearSelection]);

  const handleToolbarForward = useCallback(() => {
    if (selectedIds.size > 0) {
      setForwardingMessageIds([...selectedIds]);
      clearSelection();
    }
  }, [selectedIds, clearSelection]);

  const handleToolbarCopy = useCallback(() => {
    const selectedMessages = messages
      .filter(m => selectedIds.has(m.id))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const text = selectedMessages
      .map(m => m.text)
      .filter(Boolean)
      .join('\n\n');

    if (text) {
      navigator.clipboard.writeText(text);
    }
    clearSelection();
  }, [messages, selectedIds, clearSelection]);

  const handleToolbarDelete = useCallback(() => {
    // Delete only own messages
    const ownSelectedIds = messages
      .filter(m => selectedIds.has(m.id) && m.isOutgoing)
      .map(m => m.id);

    ownSelectedIds.forEach(id => onDeleteMessage(id));
    clearSelection();
  }, [messages, selectedIds, onDeleteMessage, clearSelection]);

  const handleToolbarMarkPublic = useCallback(() => {
    setPublishingMessageIds([...selectedIds]);
    clearSelection();
  }, [selectedIds, clearSelection]);

  // Check if selected messages can be deleted (only own messages)
  const canDeleteSelected = messages
    .filter(m => selectedIds.has(m.id))
    .every(m => m.isOutgoing);

  // No chat selected
  if (!chat) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        Выберите чат для просмотра сообщений
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col relative overflow-hidden min-w-0">
      {/* Selection toolbar */}
      <SelectionToolbar
        selectedCount={selectedIds.size}
        onClear={clearSelection}
        onReply={handleToolbarReply}
        onForward={handleToolbarForward}
        onCopy={handleToolbarCopy}
        onDelete={handleToolbarDelete}
        onMarkPublic={handleToolbarMarkPublic}
        canDelete={canDeleteSelected}
        isLoading={isSending}
      />

      {/* Chat header */}
      <TooltipProvider delayDuration={200}>
        <div className="border-b px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <ChatAvatar chatId={chat.id} title={chat.title} className="h-10 w-10" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h2 className="font-semibold truncate">{chat.title}</h2>
              {chat.linkedUserId && (
                <HoverCard openDelay={300} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <button type="button" className="inline-flex flex-shrink-0 cursor-pointer">
                      <MastersClubIcon className="h-4 w-4 text-primary" />
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent side="bottom" align="start" className="w-72">
                    {linkedUserResult.query.isLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : linkedUser ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold">
                            {[linkedUser.firstName, linkedUser.lastName].filter(Boolean).join(' ') || 'Без имени'}
                          </p>
                          {linkedUser.subscriptions?.[0] && (
                            <Badge
                              variant={linkedUser.subscriptions[0].status === 'active' ? 'default' : 'secondary'}
                              className={linkedUser.subscriptions[0].status === 'active' ? 'bg-green-500/10 text-green-500' : ''}
                            >
                              {linkedUser.subscriptions[0].status === 'active' ? 'Активна' : 'Неактивна'}
                            </Badge>
                          )}
                        </div>
                        {linkedUser.username && (
                          <p className="text-xs text-muted-foreground">@{linkedUser.username}</p>
                        )}
                        {linkedUser.phone && (
                          <p className="text-xs text-muted-foreground">{linkedUser.phone}</p>
                        )}
                        <div className="flex gap-3 pt-1 text-xs text-muted-foreground">
                          {linkedUser._count?.payments !== undefined && (
                            <span>Платежи: {linkedUser._count.payments}</span>
                          )}
                          {linkedUser.accessibleCourses?.length > 0 && (
                            <span>Курсы: {linkedUser.accessibleCourses.length}</span>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 h-7 w-full gap-1.5 text-xs"
                          onClick={() => window.open(`/users/show/${chat.linkedUserId}`, '_blank')}
                        >
                          <ExternalLink className="h-3 w-3" />
                          Открыть профиль
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Пользователь не найден</p>
                    )}
                  </HoverCardContent>
                </HoverCard>
              )}
            </div>
            {typingText ? (
              <p className="text-sm text-blue-500 animate-pulse">{typingText}</p>
            ) : chat.username ? (
              <p className="text-sm text-muted-foreground">@{chat.username}</p>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {/* Ссылка на пользователя в системе */}
            {chat.linkedUserId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5"
                    onClick={() => window.open(`/users/show/${chat.linkedUserId}`, '_blank')}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span className="text-xs">Профиль</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Открыть профиль пользователя в системе
                </TooltipContent>
              </Tooltip>
            )}
            {/* Ссылка на заказ */}
            {chat.linkedOrderId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => window.open(`/orders/${chat.linkedOrderId}/show`, '_blank')}
                  >
                    Заказ #{chat.linkedOrderId}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Открыть заказ
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </TooltipProvider>

      {/* Messages */}
      <MessageList
        messages={messages}
        chatId={chat.id}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        selectedIds={selectedIds}
        onToggleSelection={handleToggleSelection}
        onContextMenu={handleContextMenu}
        isMessageRead={isMessageRead}
        onLoadMore={onLoadMore}
      />

      {/* Message composer */}
      <MessageComposer
        onSend={onSendMessage}
        onSendFile={onSendFile}
        onSendSticker={onSendSticker}
        onSendGif={onSendGif}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        isLoading={isSending}
        isUploadingFile={isUploadingFile}
      />

      {/* Context menu */}
      {contextMenu && (
        <ContextMenu
          message={contextMenu.message}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
          onReply={handleReply}
          onForward={handleForward}
          onCopy={handleCopy}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onMarkPublic={handleMarkPublicSingle}
          onLinkToOrder={handleLinkToOrderSingle}
          canEdit={contextMenu.message.isOutgoing}
          canDelete={contextMenu.message.isOutgoing}
        />
      )}

      {/* Edit message modal */}
      <EditMessageModal
        open={editingMessage !== null}
        onClose={() => setEditingMessage(null)}
        message={editingMessage}
        onEdit={onEditMessage}
      />

      {/* Forward message modal */}
      <ForwardMessageModal
        open={forwardingMessageIds.length > 0}
        onClose={() => setForwardingMessageIds([])}
        messageIds={forwardingMessageIds}
        fromChatId={chat.id}
        onForward={async (messageIds, toChatId, options) => {
          if (messageIds.length === 1) {
            return onForwardMessage(messageIds[0], toChatId, options);
          }
          if (onForwardMessages) {
            return onForwardMessages(messageIds, toChatId, options);
          }
          // Fallback: forward one by one
          for (const id of messageIds) {
            await onForwardMessage(id, toChatId, options);
          }
        }}
      />

      {/* Publish with order modal */}
      <PublishWithOrderModal
        open={publishingMessageIds.length > 0}
        onClose={() => setPublishingMessageIds([])}
        messageIds={publishingMessageIds}
        chatTitle={chat.title ?? 'Чат'}
        currentOrderId={chat.linkedOrderId}
        onPublish={async (messageIds, orderId) => {
          await onMarkPublic(messageIds, orderId);
          setPublishingMessageIds([]);
        }}
      />
    </div>
  );
}
